import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${API_URL}/api`,
  headers: {
    'Content-Type': 'application/json'
  }
});

// Interceptor: attach token and fix Content-Type for FormData
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  // Let browser set Content-Type automatically for FormData (includes boundary)
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  return config;
});

// Interceptor: handle 401 by clearing auth
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Clients
export const getClients = () => api.get('/clients');
export const getClient = (id) => api.get(`/clients/${id}`);
export const createClient = (data) => api.post('/clients', data);
export const updateClient = (id, data) => api.put(`/clients/${id}`, data);
export const deleteClient = (id) => api.delete(`/clients/${id}`);

// Services
export const getServices = () => api.get('/services');
export const getService = (id) => api.get(`/services/${id}`);
export const createService = (data) => api.post('/services', data);
export const updateService = (id, data) => api.put(`/services/${id}`, data);
export const deleteService = (id) => api.delete(`/services/${id}`);

// Articles (productos con stock/referencia)
export const getArticles = () => api.get('/articles');
export const getArticle = (id) => api.get(`/articles/${id}`);
export const createArticle = (data) => api.post('/articles', data);
export const updateArticle = (id, data) => api.put(`/articles/${id}`, data);
export const deleteArticle = (id) => api.delete(`/articles/${id}`);
export const adjustArticleStock = (id, delta, reason) => api.post(`/articles/${id}/adjust-stock`, { delta, reason });

// Invoice Templates (DESIGN)
export const getInvoiceTemplates = () => api.get('/invoice-templates');
export const createInvoiceTemplate = (data) => api.post('/invoice-templates', data);
export const updateInvoiceTemplate = (id, data) => api.put(`/invoice-templates/${id}`, data);
export const deleteInvoiceTemplate = (id) => api.delete(`/invoice-templates/${id}`);
export const setDefaultInvoiceTemplate = (id) => api.post(`/invoice-templates/${id}/set-default`);
export const cloneInvoiceTemplate = (id) => api.post(`/invoice-templates/${id}/clone`);
export const previewInvoiceTemplate = (id) => api.get(`/invoice-templates/${id}/preview`, { responseType: 'blob' });

// Invoice Content Templates (REUSABLE)
export const getContentTemplates = () => api.get('/invoice-content-templates');
export const createContentTemplate = (data) => api.post('/invoice-content-templates', data);
export const updateContentTemplate = (id, data) => api.put(`/invoice-content-templates/${id}`, data);
export const deleteContentTemplate = (id) => api.delete(`/invoice-content-templates/${id}`);

// Invoices
export const getInvoices = (params) => api.get('/invoices', { params });
export const getInvoice = (id) => api.get(`/invoices/${id}`);
export const createInvoice = (data) => api.post('/invoices', data);
export const updateInvoice = (id, data) => api.put(`/invoices/${id}`, data);
export const deleteInvoice = (id) => api.delete(`/invoices/${id}`);
export const emitInvoice = (id, payload = {}) => api.post(`/invoices/${id}/emit`, payload);
export const voidInvoice = (id, reason) => api.post(`/invoices/${id}/void`, { reason });
export const rectifyInvoice = (id, payload) => api.post(`/invoices/${id}/rectify`, payload);
export const getNumberingGaps = (params = {}) => api.get('/invoices/numbering/gaps', { params });
export const getInvoiceDuplicates = () => api.get('/invoices/duplicates');
export const dedupeInvoices = () => api.post('/invoices/dedupe');
export const createDeliveryNoteFromInvoice = (id) => api.post(`/invoices/${id}/delivery-note`);
export const getInvoiceSeries = () => api.get('/invoice-series');
export const upsertInvoiceSeries = (data) => api.post('/invoice-series', data);

// Proformas
export const getProformas = (params = {}) => api.get('/proformas', { params });
export const getProforma = (id) => api.get(`/proformas/${id}`);
export const createProforma = (data) => api.post('/proformas', data);
export const updateProforma = (id, data) => api.put(`/proformas/${id}`, data);
export const deleteProforma = (id) => api.delete(`/proformas/${id}`);
export const convertProformaToInvoice = (id) => api.post(`/proformas/${id}/convert-to-invoice`);
export const getProformaPdfUrl = (id) => withToken(`${API_URL}/api/proformas/${id}/pdf`);

// Client 360
export const getClient360 = (id) => api.get(`/clients/${id}/360`);
// Helper: append token to URL for direct browser access (PDFs)
const withToken = (url) => {
  const token = localStorage.getItem('access_token');
  return token ? `${url}?token=${token}` : url;
};

export const getInvoicePdfUrl = (id) => withToken(`${API_URL}/api/invoices/${id}/pdf`);

// Quotes
export const getQuotes = (params) => api.get('/quotes', { params });
export const getQuote = (id) => api.get(`/quotes/${id}`);
export const createQuote = (data) => api.post('/quotes', data);
export const updateQuote = (id, data) => api.put(`/quotes/${id}`, data);
export const deleteQuote = (id) => api.delete(`/quotes/${id}`);
export const convertQuoteToInvoice = (id) => api.post(`/quotes/${id}/convert-to-invoice`);
export const getQuotePdfUrl = (id) => withToken(`${API_URL}/api/quotes/${id}/pdf`);

// Delivery Notes
export const getDeliveryNotes = (params) => api.get('/delivery-notes', { params });
export const getDeliveryNote = (id) => api.get(`/delivery-notes/${id}`);
export const createDeliveryNote = (data) => api.post('/delivery-notes', data);
export const deleteDeliveryNote = (id) => api.delete(`/delivery-notes/${id}`);
export const convertDeliveryToInvoice = (id) => api.post(`/delivery-notes/${id}/convert-to-invoice`);
export const getDeliveryNotePdfUrl = (id) => withToken(`${API_URL}/api/delivery-notes/${id}/pdf`);

// Dashboard
export const getDashboardStats = (params = {}) => api.get('/dashboard/stats', { params });

// History
export const getHistory = (year) => api.get('/history', { params: { year } });

// Settings
export const getSettings = () => api.get('/settings');
export const updateSettings = (data) => api.put('/settings', data);

// Company Settings
export const getCompanySettings = () => api.get('/company');
export const updateCompanySettings = (data) => api.put('/company', data);
export const uploadCompanyLogo = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/company/logo', formData);
};
export const getCompanyLogoUrl = () => withToken(`${API_URL}/api/company/logo`);
export const deleteCompanyLogo = () => api.delete('/company/logo');

// Auxiliar AI
export const sendAuxiliarMessage = (message, context) => 
  api.post('/auxiliar/chat', { message, context });

export const executeAuxiliarAction = (actionData) => 
  api.post('/auxiliar/execute', actionData);

export const transcribeAudio = async (audioBlob) => {
  const formData = new FormData();
  formData.append('audio', audioBlob, 'audio.webm');
  return api.post('/auxiliar/transcribe', formData);
};

// Expenses (Gastos Deducibles)
export const getExpenses = (params) => api.get('/expenses', { params });
export const getExpense = (id) => api.get(`/expenses/${id}`);
export const createExpense = (data) => api.post('/expenses', data);
export const updateExpense = (id, data) => api.put(`/expenses/${id}`, data);
export const deleteExpense = (id) => api.delete(`/expenses/${id}`);
export const getExpenseCategories = () => api.get('/expenses/categories');
export const getExpensesSummary = (year) => api.get('/expenses/summary', { params: { year } });
export const ocrExpense = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/expenses/ocr', formData);
};
export const uploadExpenseReceipt = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/expenses/upload-receipt', formData);
};

// Contracts
export const getContracts = (params) => api.get('/contracts', { params });
export const getContract = (id) => api.get(`/contracts/${id}`);
export const createContract = (data) => api.post('/contracts', data);
export const updateContract = (id, data) => api.put(`/contracts/${id}`, data);
export const deleteContract = (id) => api.delete(`/contracts/${id}`);
export const generateContract = (data) => api.post('/contracts/generate', data);
export const getContractPdfUrl = (id) => withToken(`${API_URL}/api/contracts/${id}/pdf`);

// Excel Export - download with token auth
const downloadExcel = async (endpoint, filename) => {
  const token = localStorage.getItem('access_token');
  const response = await fetch(`${API_URL}/api/export/${endpoint}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {}
  });
  if (!response.ok) throw new Error('Export failed');
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  a.remove();
};
export const exportInvoicesExcel = () => downloadExcel('invoices', 'facturas.xlsx');
export const exportProformasExcel = () => downloadExcel('proformas', 'proformas.xlsx');
export const exportExpensesExcel = () => downloadExcel('expenses', 'gastos.xlsx');
export const exportClientsExcel = () => downloadExcel('clients', 'clientes.xlsx');

// Admin Companies
export const getAdminCompanies = () => api.get('/admin/companies');
export const createAdminCompany = (data) => api.post('/admin/companies', data);
export const updateAdminCompany = (id, data) => api.put(`/admin/companies/${id}`, data);
export const deleteAdminCompany = (id) => api.delete(`/admin/companies/${id}`);

// Providers
export const getProviders = (params) => api.get('/providers', { params });
export const getProvider = (id) => api.get(`/providers/${id}`);
export const createProvider = (data) => api.post('/providers', data);
export const updateProvider = (id, data) => api.put(`/providers/${id}`, data);
export const deleteProvider = (id) => api.delete(`/providers/${id}`);
export const getProviderExpenses = (id) => api.get(`/providers/${id}/expenses`);

// Banking
export const getBankingSummary = () => api.get('/banking/summary');
export const getBankTransactions = (params) => api.get('/banking/transactions', { params });
export const createBankTransaction = (data) => api.post('/banking/transactions', data);
export const updateBankTransaction = (id, data) => api.put(`/banking/transactions/${id}`, data);
export const deleteBankTransaction = (id) => api.delete(`/banking/transactions/${id}`);
export const reconcileTransaction = (id, data) => api.post(`/banking/reconcile/${id}`, data);
export const importBankCsv = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  return api.post('/banking/import-csv', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
};
export const autoReconcile = () => api.post('/banking/auto-reconcile');
export const getBankCashflow = (year) => api.get('/banking/cashflow', { params: { year } });
export const classifyTransactions = () => api.post('/banking/classify');

// Verifactu
export const getVerifactuStatus = () => api.get('/verifactu/status');
export const generateVerifactuHashes = () => api.post('/verifactu/generate-hashes');
export const getVerifactuInvoice = (id) => api.get(`/verifactu/invoice/${id}`);

// Cash Flow
export const getCashflowCategories = () => api.get('/cashflow/categories');
export const createCashflowCategory = (data) => api.post('/cashflow/categories', data);
export const updateCashflowCategory = (id, data) => api.put(`/cashflow/categories/${id}`, data);
export const deleteCashflowCategory = (id) => api.delete(`/cashflow/categories/${id}`);
export const getCashflowScenarios = () => api.get('/cashflow/scenarios');
export const createCashflowScenario = (data) => api.post('/cashflow/scenarios', data);
export const updateCashflowScenario = (id, data) => api.put(`/cashflow/scenarios/${id}`, data);
export const deleteCashflowScenario = (id) => api.delete(`/cashflow/scenarios/${id}`);
export const createCashflowEntry = (data) => api.post('/cashflow/entries', data);
export const updateCashflowEntry = (id, data) => api.put(`/cashflow/entries/${id}`, data);
export const deleteCashflowEntry = (id) => api.delete(`/cashflow/entries/${id}`);
export const getCashflowRecurring = () => api.get('/cashflow/recurring');
export const createCashflowRecurring = (data) => api.post('/cashflow/recurring', data);
export const updateCashflowRecurring = (id, data) => api.put(`/cashflow/recurring/${id}`, data);
export const deleteCashflowRecurring = (id) => api.delete(`/cashflow/recurring/${id}`);
export const getCashflowProjection = (params) => api.get('/cashflow/projection', { params });
export const getCashflowChart = (params) => api.get('/cashflow/chart', { params });
export const getScenarioModifiers = (scenarioId) => api.get(`/cashflow/scenarios/${scenarioId}/modifiers`);
export const createScenarioModifier = (scenarioId, data) => api.post(`/cashflow/scenarios/${scenarioId}/modifiers`, data);
export const updateScenarioModifier = (modId, data) => api.put(`/cashflow/modifiers/${modId}`, data);
export const deleteScenarioModifier = (modId) => api.delete(`/cashflow/modifiers/${modId}`);
export const duplicateScenario = (scenarioId, data) => api.post(`/cashflow/scenarios/${scenarioId}/duplicate`, data);
export const compareCashflowScenarios = (params) => api.get('/cashflow/compare', { params });

// Tax / Fiscal
export const getTaxConfig = () => api.get('/tax/config');
export const updateTaxConfig = (data) => api.put('/tax/config', data);
export const getTaxCalendar = () => api.get('/tax/calendar');
export const generateTaxForm = (data) => api.post('/tax/forms/generate', data);
export const getTaxForms = (params) => api.get('/tax/forms', { params });
export const getTaxForm = (id) => api.get(`/tax/forms/${id}`);
export const adjustTaxForm = (id, data) => api.put(`/tax/forms/${id}/adjust`, data);
export const submitTaxForm = (id) => api.put(`/tax/forms/${id}/submit`);
export const getLibroEmitidas = (params) => api.get('/tax/libro/emitidas', { params });
export const getLibroRecibidas = (params) => api.get('/tax/libro/recibidas', { params });
export const getAgingReport = () => api.get('/tax/aging');

// Expense OCR Inbox
export const uploadExpenseOcr = async (files, source = 'web') => {
  const formData = new FormData();
  const fileArray = Array.isArray(files) ? files : [files];
  fileArray.forEach((f) => formData.append('files', f));
  return api.post(`/expense-uploads?source=${source}`, formData);
};
export const getExpenseUploads = (params) => api.get('/expense-uploads', { params });
export const getExpenseUpload = (id) => api.get(`/expense-uploads/${id}`);
export const reprocessExpenseUpload = (id) => api.post(`/expense-uploads/${id}/reprocess`);
export const confirmExpenseUpload = (id, data) => api.post(`/expense-uploads/${id}/confirm`, data);
export const deleteExpenseUpload = (id) => api.delete(`/expense-uploads/${id}`);

// Client Portal — Admin
export const getPortalAccess = (clientId) => api.get(`/portal/admin/access/${clientId}`);
export const enablePortalAccess = (clientId) => api.post(`/portal/admin/access/${clientId}`);
export const revokePortalAccess = (clientId) => api.delete(`/portal/admin/access/${clientId}`);
export const rotatePortalAccess = (clientId) => api.post(`/portal/admin/access/${clientId}/rotate`);
export const getPortalSettings = () => api.get('/portal/admin/settings');
export const updatePortalSettings = (data) => api.put('/portal/admin/settings', data);
export const getPortalNotifications = (unreadOnly = false) => api.get('/portal/admin/notifications', { params: { unread_only: unreadOnly } });
export const markPortalNotificationRead = (id) => api.post(`/portal/admin/notifications/${id}/read`);
export const markAllPortalNotificationsRead = () => api.post('/portal/admin/notifications/read-all');
export const adminSendClientMessage = (clientId, content) => api.post(`/portal/admin/${clientId}/messages`, { content });
export const adminListClientMessages = (clientId) => api.get(`/portal/admin/${clientId}/messages`);

// Client Portal — Public (no auth header needed but instance uses it; we create a fresh instance)
const portalApi = axios.create({
  baseURL: `${API_URL}/api/portal`,
  headers: { 'Content-Type': 'application/json' },
});
export const portalMe = (token) => portalApi.get(`/${token}/me`);
export const portalSummary = (token) => portalApi.get(`/${token}/summary`);
export const portalInvoices = (token, params) => portalApi.get(`/${token}/invoices`, { params });
export const portalInvoiceDetail = (token, id) => portalApi.get(`/${token}/invoices/${id}`);
export const portalInvoicePdfUrl = (token, id) => `${API_URL}/api/portal/${token}/invoices/${id}/pdf`;
export const portalQuotes = (token) => portalApi.get(`/${token}/quotes`);
export const portalQuoteDetail = (token, id) => portalApi.get(`/${token}/quotes/${id}`);
export const portalQuotePdfUrl = (token, id) => `${API_URL}/api/portal/${token}/quotes/${id}/pdf`;
export const portalQuoteRespond = (token, id, action, comment) => portalApi.post(`/${token}/quotes/${id}/respond`, { action, comment });
export const portalMessages = (token) => portalApi.get(`/${token}/messages`);
export const portalSendMessage = (token, content) => portalApi.post(`/${token}/messages`, { content });
export const portalLogoUrl = (token) => `${API_URL}/api/portal/${token}/logo`;

// Portal Magic Link
export const portalMagicRequest = (email) => portalApi.post('/magic/request', { email });
export const portalMagicExchange = (magicToken) => portalApi.get(`/magic/${magicToken}`);

// Payments (Public)
const payApi = axios.create({ baseURL: `${API_URL}/api/pay`, headers: { 'Content-Type': 'application/json' } });
export const payInvoiceInfo = (paymentToken) => payApi.get(`/invoice/${paymentToken}`);
export const payInvoiceCheckout = (paymentToken, originUrl) => payApi.post(`/invoice/${paymentToken}/checkout`, { origin_url: originUrl });
export const payStatus = (sessionId) => payApi.get(`/status/${sessionId}`);

// Payments (Admin)
export const getPaymentLink = (invoiceId) => api.get(`/payments/invoice/${invoiceId}/link`);
export const togglePayment = (invoiceId, enabled) => api.post(`/payments/invoice/${invoiceId}/toggle`, { payment_enabled: enabled });
export const listPaymentTransactions = () => api.get('/payments/transactions');

// Dunning
export const getDunningSequences = () => api.get('/dunning/sequences');
export const createDunningSequence = (data) => api.post('/dunning/sequences', data);
export const updateDunningSequence = (id, data) => api.put(`/dunning/sequences/${id}`, data);
export const deleteDunningSequence = (id) => api.delete(`/dunning/sequences/${id}`);
export const getInvoiceDunning = (invoiceId) => api.get(`/dunning/invoice/${invoiceId}`);
export const startInvoiceDunning = (invoiceId) => api.post(`/dunning/invoice/${invoiceId}/start`);
export const pauseInvoiceDunning = (invoiceId) => api.post(`/dunning/invoice/${invoiceId}/pause`);
export const unpauseInvoiceDunning = (invoiceId) => api.post(`/dunning/invoice/${invoiceId}/unpause`);
export const sendDunningNow = (invoiceId, data) => api.post(`/dunning/invoice/${invoiceId}/send-now`, data);
export const dunningTick = () => api.post('/dunning/tick');
export const dunningDashboard = () => api.get('/dunning/dashboard');

// Banking PSD2
export const psd2Provider = () => api.get('/banking-psd2/provider');
export const psd2Institutions = (country = 'ES') => api.get('/banking-psd2/institutions', { params: { country } });
export const psd2Connect = (institutionId, redirectUrl) => api.post('/banking-psd2/connect', { institution_id: institutionId, redirect_url: redirectUrl });
export const psd2Finalize = (requisitionId) => api.post(`/banking-psd2/finalize/${requisitionId}`);
export const psd2Accounts = () => api.get('/banking-psd2/accounts');
export const psd2SyncAccount = (accountId) => api.post(`/banking-psd2/accounts/${accountId}/sync`);
export const psd2Transactions = (params) => api.get('/banking-psd2/transactions', { params });
export const psd2ConfirmMatch = (txnId, data) => api.post(`/banking-psd2/transactions/${txnId}/confirm`, data);

// Subscriptions
export const listSubscriptions = (params) => api.get('/subscriptions', { params });
export const getSubscription = (id) => api.get(`/subscriptions/${id}`);
export const createSubscription = (data) => api.post('/subscriptions', data);
export const updateSubscription = (id, data) => api.put(`/subscriptions/${id}`, data);
export const deleteSubscription = (id) => api.delete(`/subscriptions/${id}`);
export const pauseSubscription = (id, until) => api.post(`/subscriptions/${id}/pause`, { until });
export const resumeSubscription = (id) => api.post(`/subscriptions/${id}/resume`);
export const cancelSubscription = (id, mode) => api.post(`/subscriptions/${id}/cancel`, { mode });
export const reactivateSubscription = (id) => api.post(`/subscriptions/${id}/reactivate`);
export const changeSubscriptionPlan = (id, items, effective_at) => api.post(`/subscriptions/${id}/change-plan`, { items, effective_at });
export const generateInvoiceNow = (id) => api.post(`/subscriptions/${id}/generate-now`);
export const subscriptionEvents = (id) => api.get(`/subscriptions/${id}/events`);
export const subscriptionInvoices = (id) => api.get(`/subscriptions/${id}/invoices`);
export const subscriptionsDashboard = () => api.get('/subscriptions/dashboard');
export const subscriptionsUpcoming = (days = 14) => api.get('/subscriptions/upcoming', { params: { days } });
export const subscriptionsTick = () => api.post('/subscriptions/tick');

// SEPA
export const listSepaMandates = (clientId) => api.get('/sepa/mandates', { params: clientId ? { client_id: clientId } : {} });
export const createSepaMandate = (data) => api.post('/sepa/mandates', data);
export const revokeSepaMandate = (id) => api.delete(`/sepa/mandates/${id}`);
export const listSepaBatches = () => api.get('/sepa/batches');
export const generateSepaBatch = () => api.post('/sepa/batches/generate');
export const sepaBatchDownloadUrl = (id) => `${API_URL}/api/sepa/batches/${id}/download`;

// Projects
export const listProjects = (params) => api.get('/projects', { params });
export const getProject = (id) => api.get(`/projects/${id}`);
export const createProject = (data) => api.post('/projects', data);
export const updateProject = (id, data) => api.put(`/projects/${id}`, data);
export const deleteProject = (id) => api.delete(`/projects/${id}`);
export const projectProfitability = (id) => api.get(`/projects/${id}/profitability`);

// Time entries
export const listTimeEntries = (params) => api.get('/time-entries', { params });
export const createTimeEntry = (data) => api.post('/time-entries', data);
export const updateTimeEntry = (id, data) => api.put(`/time-entries/${id}`, data);
export const deleteTimeEntry = (id) => api.delete(`/time-entries/${id}`);
export const getActiveTimer = () => api.get('/time-entries/active');
export const startTimer = (data) => api.post('/time-entries/start', data);
export const stopTimer = (data) => api.post('/time-entries/stop', data || {});
export const resolveHourlyRate = (params) => api.get('/time-entries/resolve-rate', { params });
export const invoiceTimeEntries = (data) => api.post('/time-entries/invoice', data);

// Profitability
export const profitabilityDashboard = () => api.get('/profitability/dashboard');
export const profitabilityClients = () => api.get('/profitability/clients');
export const profitabilityServices = () => api.get('/profitability/services');
export const profitabilityAlerts = () => api.get('/profitability/alerts');

export default api;
